import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
  useMemo,
} from 'react';

export type ActivityGraphTab = 'activity' | 'emails' | 'documents';

export interface ActivityGraphState {
  activeTab: ActivityGraphTab;
  expandedEmailIds: Set<string>;
  highlightedEmailId: string | null;
  expandedDocumentId: string | null;
  highlightedDocumentId: string | null;
}

export interface ActivityGraphActions {
  setActiveTab: (tab: ActivityGraphTab) => void;
  viewEmail: (emailId: string) => void;
  viewEmailThread: (emailIds: string[]) => void;
  toggleEmailExpanded: (emailId: string) => void;
  expandAllEmails: (emailIds: string[]) => void;
  collapseAllEmails: () => void;
  viewDocument: (documentId: string) => void;
  toggleDocumentExpanded: (documentId: string) => void;
  clearHighlights: () => void;
}

export interface ActivityGraphContextValue
  extends ActivityGraphState, ActivityGraphActions {}

const ActivityGraphContext = createContext<ActivityGraphContextValue | null>(
  null,
);

interface ActivityGraphProviderProps {
  children: ReactNode;
  defaultTab?: ActivityGraphTab;
}

export function ActivityGraphProvider({
  children,
  defaultTab = 'activity',
}: ActivityGraphProviderProps) {
  const [activeTab, setActiveTab] = useState<ActivityGraphTab>(defaultTab);
  const [expandedEmailIds, setExpandedEmailIds] = useState<Set<string>>(
    new Set(),
  );
  const [highlightedEmailId, setHighlightedEmailId] = useState<string | null>(
    null,
  );
  const [expandedDocumentId, setExpandedDocumentId] = useState<string | null>(
    null,
  );
  const [highlightedDocumentId, setHighlightedDocumentId] = useState<
    string | null
  >(null);

  const viewEmail = useCallback((emailId: string) => {
    setActiveTab('emails');
    setExpandedEmailIds((prev) => new Set([...prev, emailId]));
    setHighlightedEmailId(emailId);
  }, []);

  const viewEmailThread = useCallback((emailIds: string[]) => {
    setActiveTab('emails');
    setExpandedEmailIds(new Set(emailIds));
    if (emailIds.length > 0) {
      setHighlightedEmailId(emailIds[emailIds.length - 1]);
    }
  }, []);

  const toggleEmailExpanded = useCallback((emailId: string) => {
    setExpandedEmailIds((prev) => {
      const next = new Set(prev);
      if (next.has(emailId)) {
        next.delete(emailId);
      } else {
        next.add(emailId);
      }
      return next;
    });
  }, []);

  const expandAllEmails = useCallback((emailIds: string[]) => {
    setExpandedEmailIds(new Set(emailIds));
  }, []);

  const collapseAllEmails = useCallback(() => {
    setExpandedEmailIds(new Set());
  }, []);

  const viewDocument = useCallback((documentId: string) => {
    setActiveTab('documents');
    setExpandedDocumentId(documentId);
    setHighlightedDocumentId(documentId);
  }, []);

  const toggleDocumentExpanded = useCallback((documentId: string) => {
    setExpandedDocumentId((prev) => (prev === documentId ? null : documentId));
  }, []);

  const clearHighlights = useCallback(() => {
    setHighlightedEmailId(null);
    setHighlightedDocumentId(null);
  }, []);

  const value = useMemo<ActivityGraphContextValue>(
    () => ({
      // State
      activeTab,
      expandedEmailIds,
      highlightedEmailId,
      expandedDocumentId,
      highlightedDocumentId,
      // Actions
      setActiveTab,
      viewEmail,
      viewEmailThread,
      toggleEmailExpanded,
      expandAllEmails,
      collapseAllEmails,
      viewDocument,
      toggleDocumentExpanded,
      clearHighlights,
    }),
    [
      activeTab,
      expandedEmailIds,
      highlightedEmailId,
      expandedDocumentId,
      highlightedDocumentId,
      viewEmail,
      viewEmailThread,
      toggleEmailExpanded,
      expandAllEmails,
      collapseAllEmails,
      viewDocument,
      toggleDocumentExpanded,
      clearHighlights,
    ],
  );

  return (
    <ActivityGraphContext.Provider value={value}>
      {children}
    </ActivityGraphContext.Provider>
  );
}

/**
 * Hook to access ActivityGraphContext.
 * Returns null when used outside of ActivityGraphProvider.
 * Use this for optional context consumption (backwards compatibility).
 */
export function useActivityGraphContext(): ActivityGraphContextValue | null {
  return useContext(ActivityGraphContext);
}

/**
 * Hook to access ActivityGraphContext.
 * Throws an error if used outside of ActivityGraphProvider.
 * Use this when context is required.
 */
export function useRequiredActivityGraphContext(): ActivityGraphContextValue {
  const context = useContext(ActivityGraphContext);
  if (!context) {
    throw new Error(
      'useRequiredActivityGraphContext must be used within ActivityGraphProvider',
    );
  }
  return context;
}
